import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import api, { smsAPI } from '../../services/api';
import { 
  MessageSquare, 
  CheckCircle, 
  XCircle, 
  Clock, 
  RefreshCw,
  Trash2,
  Send,
  Phone,
  AlertCircle,
  Smartphone,
  TrendingUp,
  Percent,
  Plus,
  Edit,
  Archive,
  RotateCcw
} from 'lucide-react';

const SMSLogsTab = () => {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [configStatus, setConfigStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showManualForm, setShowManualForm] = useState(false);
  const [manualSMS, setManualSMS] = useState({ phone: '', message: '', recipient_name: '' });
  const [sending, setSending] = useState(false);
  const [editModal, setEditModal] = useState({ open: false, sms: null });
  const [editData, setEditData] = useState({});

  const fetchData = async () => {
    try {
      setLoading(true);
      const [logsRes, statsRes, configRes] = await Promise.all([
        smsAPI.getLogs(100, filter === 'all' ? null : filter),
        smsAPI.getStats(),
        smsAPI.getConfigStatus()
      ]);
      setLogs(logsRes.data.sms_logs || []);
      setStats(statsRes.data);
      setConfigStatus(configRes.data);
    } catch (error) {
      console.error('Failed to fetch SMS data:', error);
      toast.error('Failed to load SMS data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filter]);

  const handleSendManualSMS = async (e) => {
    e.preventDefault();
    if (!manualSMS.phone.trim() || !manualSMS.message.trim()) {
      toast.error('Please enter phone number and message');
      return;
    }

    setSending(true);
    try {
      await smsAPI.sendTest(manualSMS.phone, manualSMS.message);
      toast.success('SMS sent successfully!');
      setManualSMS({ phone: '', message: '', recipient_name: '' });
      setShowManualForm(false);
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(detail?.message || 'Failed to send SMS');
    } finally {
      setSending(false);
    }
  };

  const handleResendSMS = async (log) => {
    if (!window.confirm(`Resend SMS to ${log.to_phone}?`)) return;
    setSending(true);
    try {
      await smsAPI.sendTest(log.to_phone, log.message_preview);
      toast.success('SMS resent successfully!');
      fetchData();
    } catch (error) {
      toast.error('Failed to resend SMS');
    } finally {
      setSending(false);
    }
  };

  const handleArchiveSMS = async (logId) => {
    if (!window.confirm('Archive this SMS log?')) return;
    try {
      await api.post(`/sms/logs/${logId}/archive`);
      toast.success('SMS log archived');
      setLogs(prev => prev.filter(l => l.id !== logId));
    } catch (error) {
      toast.error('Failed to archive SMS log');
    }
  };

  const handleDeleteLog = async (logId) => {
    if (!window.confirm('Delete this SMS log permanently?')) return;
    try {
      await smsAPI.deleteLog(logId);
      toast.success('Log deleted');
      setLogs(prev => prev.filter(l => l.id !== logId));
    } catch (error) {
      toast.error('Failed to delete log');
    }
  };

  const handleOpenEditModal = (sms) => {
    setEditData({
      to_phone: sms.to_phone || '',
      message_preview: sms.message_preview || '',
      recipient_name: sms.recipient_name || '',
      status: sms.status || 'sent'
    });
    setEditModal({ open: true, sms });
  };

  const handleSaveEdit = async () => {
    try {
      await api.put(`/sms/logs/${editModal.sms.id}`, editData);
      toast.success('SMS log updated');
      setEditModal({ open: false, sms: null });
      fetchData();
    } catch (error) {
      toast.error('Failed to update SMS log');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    if (status === 'sent') {
      return <Badge className="bg-[var(--success)]/15 text-[var(--success)]"><CheckCircle className="h-3 w-3 mr-1" />Sent</Badge>;
    }
    if (status === 'archived') {
      return <Badge className="bg-[var(--background-subtle)] text-[var(--text-primary)]"><Archive className="h-3 w-3 mr-1" />Archived</Badge>;
    }
    return <Badge className="bg-[var(--error)]/15 text-[var(--error)]"><XCircle className="h-3 w-3 mr-1" />Failed</Badge>;
  };

  const getSmsTypeBadge = (type) => {
    const types = {
      'backup_worker_assignment': { color: 'bg-[#D4AF37]/10 text-blue-800', label: 'Assignment' },
      'backup_callout_notification': { color: 'bg-[#F4C2C2]/15 text-purple-800', label: 'Call Out' },
      'backup_callout_approved': { color: 'bg-[var(--success)]/15 text-[var(--success)]', label: 'Approved' },
      'backup_callout_rejected': { color: 'bg-[var(--error)]/15 text-[var(--error)]', label: 'Rejected' },
      'test': { color: 'bg-[var(--background-subtle)] text-[var(--text-primary)]', label: 'Test' },
      'manual': { color: 'bg-cyan-100 text-cyan-800', label: 'Manual' },
      'notification': { color: 'bg-cyan-100 text-cyan-800', label: 'Notification' }
    };
    const typeInfo = types[type] || { color: 'bg-[var(--background-subtle)] text-[var(--text-primary)]', label: type || 'Unknown' };
    return <Badge className={typeInfo.color}>{typeInfo.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-pink-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="sms-logs-tab">
      {/* Header with Actions */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold font-['Cormorant_Garamond']">SMS Logs</h1>
          <p className="text-[var(--text-muted)]">Manage and track SMS notifications</p>
        </div>
        <Button onClick={() => setShowManualForm(true)} className="bg-pink-500 hover:bg-pink-600">
          <Plus className="w-4 h-4 mr-2" />
          Send Manual SMS
        </Button>
      </div>

      {/* Config Status Banner */}
      {configStatus && (
        <Card className={configStatus.configured ? 'bg-[var(--success)]/10 border-[var(--success)]/30' : 'bg-[var(--error)]/10 border-[var(--error)]/30'}>
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Smartphone className={`h-6 w-6 ${configStatus.configured ? 'text-[var(--success)]' : 'text-[var(--error)]'}`} />
              <div>
                <p className="font-medium">{configStatus.message}</p>
                {configStatus.phone_number && (
                  <p className="text-sm text-[var(--text-secondary)]">Sending from: ...{configStatus.phone_number}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Manual SMS Form Modal */}
      {showManualForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowManualForm(false)}>
          <div className="bg-[var(--background-paper)] rounded-xl p-6 w-full max-w-lg" onClick={e => e.stopPropagation()}>
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Send className="h-5 w-5 text-pink-500" />
              Send Manual SMS
            </h2>
            <form onSubmit={handleSendManualSMS} className="space-y-4">
              <div>
                <Label>Recipient Name (Optional)</Label>
                <Input
                  value={manualSMS.recipient_name}
                  onChange={(e) => setManualSMS({ ...manualSMS, recipient_name: e.target.value })}
                  placeholder="John Doe"
                />
              </div>
              <div>
                <Label>Phone Number *</Label>
                <Input
                  value={manualSMS.phone}
                  onChange={(e) => setManualSMS({ ...manualSMS, phone: e.target.value })}
                  placeholder="+1234567890"
                  required
                />
                <p className="text-xs text-[var(--text-muted)] mt-1">Include country code (e.g., +1 for US)</p>
              </div>
              <div>
                <Label>Message *</Label>
                <Textarea
                  value={manualSMS.message}
                  onChange={(e) => setManualSMS({ ...manualSMS, message: e.target.value })}
                  placeholder="Enter your message..."
                  rows={4}
                  required
                />
                <p className="text-xs text-[var(--text-muted)] mt-1">{manualSMS.message.length}/160 characters</p>
              </div>
              <div className="flex gap-2">
                <Button type="submit" disabled={sending} className="flex-1 bg-pink-500 hover:bg-pink-600">
                  {sending ? 'Sending...' : 'Send SMS'}
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowManualForm(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit SMS Modal */}
      {editModal.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setEditModal({ open: false, sms: null })}>
          <div className="bg-[var(--background-paper)] rounded-xl p-6 w-full max-w-lg" onClick={e => e.stopPropagation()}>
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Edit className="h-5 w-5 text-[#D4AF37]" />
              Edit SMS Log
            </h2>
            <div className="space-y-4">
              <div>
                <Label>Recipient Phone</Label>
                <Input
                  value={editData.to_phone}
                  onChange={(e) => setEditData({ ...editData, to_phone: e.target.value })}
                />
              </div>
              <div>
                <Label>Recipient Name</Label>
                <Input
                  value={editData.recipient_name}
                  onChange={(e) => setEditData({ ...editData, recipient_name: e.target.value })}
                />
              </div>
              <div>
                <Label>Message</Label>
                <Textarea
                  value={editData.message_preview}
                  onChange={(e) => setEditData({ ...editData, message_preview: e.target.value })}
                  rows={3}
                />
              </div>
              <div>
                <Label>Status</Label>
                <Select value={editData.status} onValueChange={(v) => setEditData({ ...editData, status: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sent">Sent</SelectItem>
                    <SelectItem value="failed">Failed</SelectItem>
                    <SelectItem value="archived">Archived</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2">
                <Button onClick={handleSaveEdit} className="flex-1 btn-primary">Save Changes</Button>
                <Button variant="outline" onClick={() => setEditModal({ open: false, sms: null })}>Cancel</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-cyan-50 border-blue-100">
          <CardContent className="p-4 text-center">
            <MessageSquare className="h-6 w-6 mx-auto text-[#D4AF37] mb-1" />
            <p className="text-2xl font-bold text-[#D4AF37]">{stats?.total || 0}</p>
            <p className="text-xs text-[#D4AF37]/70">Total SMS</p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-100">
          <CardContent className="p-4 text-center">
            <CheckCircle className="h-6 w-6 mx-auto text-[var(--success)] mb-1" />
            <p className="text-2xl font-bold text-[var(--success)]">{stats?.sent || 0}</p>
            <p className="text-xs text-[var(--success)]/70">Sent</p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-red-50 to-pink-50 border-red-100">
          <CardContent className="p-4 text-center">
            <XCircle className="h-6 w-6 mx-auto text-[var(--error)] mb-1" />
            <p className="text-2xl font-bold text-[var(--error)]">{stats?.failed || 0}</p>
            <p className="text-xs text-[var(--error)]/70">Failed</p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-purple-50 to-indigo-50 border-purple-100">
          <CardContent className="p-4 text-center">
            <TrendingUp className="h-6 w-6 mx-auto text-[#F4C2C2] mb-1" />
            <p className="text-2xl font-bold text-[#F4C2C2]">{stats?.today_sent || 0}</p>
            <p className="text-xs text-[#F4C2C2]/70">Sent Today</p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-100">
          <CardContent className="p-4 text-center">
            <Percent className="h-6 w-6 mx-auto text-amber-500 mb-1" />
            <p className="text-2xl font-bold text-amber-600">{stats?.success_rate || 0}%</p>
            <p className="text-xs text-amber-600/70">Success Rate</p>
          </CardContent>
        </Card>
      </div>

      {/* Filter and Logs */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <CardTitle className="text-lg flex items-center gap-2">
              <Phone className="h-5 w-5 text-pink-500" />
              SMS Logs
            </CardTitle>
            <div className="flex items-center gap-2">
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Filter" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="sent">Sent</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                </SelectContent>
              </Select>
              
              <Button variant="outline" size="sm" onClick={fetchData}>
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {logs.length === 0 ? (
            <div className="text-center py-12">
              <MessageSquare className="h-16 w-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-[var(--text-primary)]">No SMS Logs</h3>
              <p className="text-[var(--text-muted)] mt-1">SMS messages will appear here when sent</p>
            </div>
          ) : (
            <div className="space-y-3">
              {logs.map((log) => (
                <div 
                  key={log.id}
                  className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-[var(--background-subtle)] rounded-lg border hover:bg-[var(--background-subtle)] transition-colors gap-3"
                >
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className="flex-shrink-0 mt-1">
                      {log.status === 'sent' ? (
                        <CheckCircle className="h-5 w-5 text-[var(--success)]" />
                      ) : log.status === 'archived' ? (
                        <Archive className="h-5 w-5 text-[var(--text-muted)]" />
                      ) : (
                        <XCircle className="h-5 w-5 text-[var(--error)]" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-[var(--text-primary)]">{log.to_phone}</span>
                        {log.recipient_name && <span className="text-sm text-[var(--text-muted)]">({log.recipient_name})</span>}
                        {getStatusBadge(log.status)}
                        {getSmsTypeBadge(log.sms_type)}
                      </div>
                      <p className="text-sm text-[var(--text-secondary)] mt-1 line-clamp-2">{log.message_preview}</p>
                      <div className="flex items-center gap-3 mt-2 text-xs text-[var(--text-muted)]">
                        <span>{formatDate(log.created_at)}</span>
                        {log.error_code && (
                          <span className="text-[var(--error)] flex items-center gap-1">
                            <AlertCircle className="h-3 w-3" />
                            {log.error_code}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleResendSMS(log)}
                      className="text-[#D4AF37] hover:text-[#D4AF37] hover:bg-[#D4AF37]/10"
                      title="Resend"
                    >
                      <RotateCcw className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleOpenEditModal(log)}
                      className="text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--background-subtle)]"
                      title="Edit"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleArchiveSMS(log.id)}
                      className="text-amber-600 hover:text-amber-700 hover:bg-[var(--warning)]/10"
                      title="Archive"
                    >
                      <Archive className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteLog(log.id)}
                      className="text-[var(--error)] hover:text-red-700 hover:bg-[var(--error)]/10"
                      title="Delete"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SMSLogsTab;
